/**
 * useCalendarDialogs Hook
 *
 * Shared hook that encapsulates dialog state and handlers for calendar views.
 * Used by WeekView to manage shift/task creation and details dialogs.
 */

import { useCallback } from 'react';
import { format } from 'date-fns';
import type { ShiftDto, ShiftListItemDto, Task, CalendarEventDto } from '@signalvote/shared';
import { useDialogState } from './useDialogState';
import { useMayManageTasks } from './useMayManageTasks';

// ============================================================================
// Types
// ============================================================================

export interface CreateDialogData {
  date: string;
  hour: number;
}

export interface ShiftDetailsDialogData {
  shiftId: string;
}

export interface TaskDetailsDialogData {
  task: Task;
}

export interface CalendarEventDetailsDialogData {
  event: CalendarEventDto;
}

export interface UseCalendarDialogsReturn {
  // Dialog states
  shiftCreateDialog: ReturnType<typeof useDialogState<CreateDialogData>>;
  shiftDetailsDialog: ReturnType<typeof useDialogState<ShiftDetailsDialogData>>;
  taskCreateDialog: ReturnType<typeof useDialogState<CreateDialogData>>;
  taskDetailsDialog: ReturnType<typeof useDialogState<TaskDetailsDialogData>>;
  calendarEventDetailsDialog: ReturnType<typeof useDialogState<CalendarEventDetailsDialogData>>;
  choiceDialog: ReturnType<typeof useDialogState<CreateDialogData>>;

  // Handlers
  handleShiftClick: (shift: ShiftDto | ShiftListItemDto) => void;
  handleTaskClick: (task: Task) => void;
  handleCalendarEventClick: (event: CalendarEventDto) => void;
  /** Absent when the user may not create shifts or tasks. */
  handleEmptySlotClick: ((date: Date, hour: number) => void) | undefined;
  handleChooseShift: () => void;
  handleChooseTask: () => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for managing calendar dialog state and handlers.
 * Encapsulates the calendar view's dialog logic.
 *
 * @example
 * const {
 *   shiftCreateDialog,
 *   shiftDetailsDialog,
 *   taskCreateDialog,
 *   taskDetailsDialog,
 *   choiceDialog,
 *   handleShiftClick,
 *   handleTaskClick,
 *   handleEmptySlotClick,
 *   handleChooseShift,
 *   handleChooseTask,
 * } = useCalendarDialogs();
 */
export function useCalendarDialogs(): UseCalendarDialogsReturn {
  const mayManageTasks = useMayManageTasks();

  // Dialog state
  const shiftCreateDialog = useDialogState<CreateDialogData>();
  const shiftDetailsDialog = useDialogState<ShiftDetailsDialogData>();
  const taskCreateDialog = useDialogState<CreateDialogData>();
  const taskDetailsDialog = useDialogState<TaskDetailsDialogData>();
  const calendarEventDetailsDialog = useDialogState<CalendarEventDetailsDialogData>();
  const choiceDialog = useDialogState<CreateDialogData>();

  // Destructure stable open/close callbacks for use in dependency arrays
  const { open: openShiftDetails } = shiftDetailsDialog;
  const { open: openTaskDetails } = taskDetailsDialog;
  const { open: openCalendarEventDetails } = calendarEventDetailsDialog;
  const { open: openChoiceDialog, close: closeChoiceDialog } = choiceDialog;
  const { open: openShiftCreate } = shiftCreateDialog;
  const { open: openTaskCreate } = taskCreateDialog;

  // Shift click handler - opens shift details dialog
  const handleShiftClick = useCallback(
    (shift: ShiftDto | ShiftListItemDto) => {
      openShiftDetails({ shiftId: shift.id });
    },
    [openShiftDetails]
  );

  // Task click handler - opens task details dialog
  const handleTaskClick = useCallback(
    (task: Task) => {
      openTaskDetails({ task });
    },
    [openTaskDetails]
  );

  // Calendar event click handler - opens event details dialog
  const handleCalendarEventClick = useCallback(
    (event: CalendarEventDto) => {
      openCalendarEventDetails({ event });
    },
    [openCalendarEventDetails]
  );

  // Empty slot click handler - opens choice dialog (shift vs task)
  const handleEmptySlotClick = useCallback(
    (date: Date, hour: number) => {
      openChoiceDialog({
        date: format(date, 'yyyy-MM-dd'),
        hour,
      });
    },
    [openChoiceDialog]
  );

  // Choice dialog handlers
  const handleChooseShift = useCallback(() => {
    if (choiceDialog.data) {
      openShiftCreate(choiceDialog.data);
    }
    closeChoiceDialog();
  }, [choiceDialog.data, openShiftCreate, closeChoiceDialog]);

  const handleChooseTask = useCallback(() => {
    if (choiceDialog.data) {
      openTaskCreate(choiceDialog.data);
    }
    closeChoiceDialog();
  }, [choiceDialog.data, openTaskCreate, closeChoiceDialog]);

  return {
    // Dialog states
    shiftCreateDialog,
    shiftDetailsDialog,
    taskCreateDialog,
    taskDetailsDialog,
    calendarEventDetailsDialog,
    choiceDialog,

    // Handlers
    handleShiftClick,
    handleTaskClick,
    handleCalendarEventClick,
    handleEmptySlotClick: mayManageTasks ? handleEmptySlotClick : undefined,
    handleChooseShift,
    handleChooseTask,
  };
}

export default useCalendarDialogs;
